import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { BarcodeScanInput } from "@/components/feature/BarcodeScanInput";
import { resolveBarcode, type BarcodeMatch } from "@/lib/barcode";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { fulfilmentWorkspacePath } from "@/lib/fulfilment-workspace";

interface BalanceRow {
  location_id: string;
  on_hand: number;
  allocated: number;
  available: number;
  incoming: number;
  locations?: { name: string } | null;
}

export default function BarcodeLookup() {
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [matches, setMatches] = useState<BarcodeMatch[] | null>(null);
  const [ambiguous, setAmbiguous] = useState(false);
  const [balances, setBalances] = useState<BalanceRow[]>([]);
  const [activeMatch, setActiveMatch] = useState<BarcodeMatch | null>(null);

  const handleScan = async (value: string) => {
    setBusy(true);
    setFeedback(null);
    setMatches(null);
    setAmbiguous(false);
    setBalances([]);
    setActiveMatch(null);
    try {
      const res = await resolveBarcode(org!.id, value);
      if (!res.found) {
        setFeedback({ ok: false, text: `No product or variant found for "${value}".` });
        return;
      }
      if (res.ambiguous) {
        setAmbiguous(true);
        setMatches(res.matches);
        setFeedback({ ok: false, text: "Multiple items match this barcode/SKU — select the correct one." });
        return;
      }
      const match = res.matches[0];
      setMatches(res.matches);
      setActiveMatch(match);
      let balQuery = supabase
        .from("inventory_balances")
        .select("location_id, on_hand, allocated, available, incoming, locations(name)")
        .eq("org_id", org!.id)
        .eq("product_id", match.product_id);
      if (match.variant_id) balQuery = balQuery.eq("variant_id", match.variant_id);
      const { data } = await balQuery;
      setBalances((data ?? []).map((row) => ({
        ...row,
        locations: firstRelation(row.locations),
      })) as BalanceRow[]);
      setFeedback({ ok: true, text: `Found: ${match.name}` });
    } catch (err) {
      setFeedback({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Barcode Lookup" subtitle="Scan an item to see what it is and where its stock is" backTo={fulfilmentWorkspacePath("picking")} />

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <BarcodeScanInput onScan={handleScan} busy={busy} feedback={feedback} />
        <p className="text-xs text-slate-400 mt-2">Scans by product barcode, variant barcode, or SKU. Most scanners act like a keyboard.</p>
      </div>

      {ambiguous && matches && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Multiple matches — pick one</h3>
          </div>
          <div className="divide-y divide-slate-50">
            {matches.map((m) => (
              <button
                key={m.id}
                onClick={() => handleScan(m.barcode ?? m.sku ?? m.id)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3"
              >
                <i className={m.kind === "variant" ? "ri-stack-line text-slate-400" : "ri-archive-line text-slate-400"} />
                <div>
                  <p className="text-sm font-medium text-slate-800">{m.name}</p>
                  <p className="text-xs text-slate-400 font-mono">
                    {m.kind === "variant" ? "Variant" : "Product"} · {m.sku ?? m.barcode ?? "—"}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeMatch && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-slate-400 uppercase tracking-wider font-semibold">
                {activeMatch.kind === "variant" ? "Variant" : "Product"}
              </p>
              <h3 className="text-xl font-semibold text-slate-900 mt-1">{activeMatch.name}</h3>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {activeMatch.sku && <span className="text-sm font-mono text-slate-600">SKU {activeMatch.sku}</span>}
                {activeMatch.barcode && <span className="text-sm font-mono text-slate-500">Barcode {activeMatch.barcode}</span>}
                <StatusBadge status="active" />
              </div>
            </div>
            <button
              onClick={() => navigate(`/products/${activeMatch.product_id}`)}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              View product
            </button>
          </div>
          <div className="p-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Stock by location</h4>
            {balances.length === 0 ? (
              <p className="text-sm text-slate-400">No stock records for this item.</p>
            ) : (
              <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Allocated</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Available</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Incoming</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balances.map((b, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="px-3 py-2.5 text-sm text-slate-700">{b.locations?.name ?? "—"}</td>
                        <td className="px-3 py-2.5 text-sm text-slate-800 text-right font-medium">{b.on_hand}</td>
                        <td className="px-3 py-2.5 text-sm text-slate-600 text-right">{b.allocated}</td>
                        <td className="px-3 py-2.5 text-sm text-emerald-700 text-right font-semibold">{b.available}</td>
                        <td className="px-3 py-2.5 text-sm text-slate-600 text-right">{b.incoming}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y divide-slate-50">
                {balances.map((b, i) => (
                  <div key={i} className="py-3 flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-700 font-medium">{b.locations?.name ?? "—"}</span>
                    <div className="text-right text-xs">
                      <p className="text-slate-500">On hand <span className="font-semibold text-slate-800">{b.on_hand}</span></p>
                      <p className="text-slate-500">Available <span className="font-semibold text-emerald-700">{b.available}</span></p>
                      <p className="text-slate-400">Alloc {b.allocated} · In {b.incoming}</p>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
            {role && (
              <p className="text-xs text-slate-400 mt-3">
                To adjust stock, use the <button onClick={() => navigate("/inventory")} className="text-emerald-600 hover:text-emerald-700 font-medium">Inventory</button> screen — scanning never changes stock on its own.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}